// «Jornada en vivo»: la brigada de la Ruta de la Salud registra a cada persona
// directo a su expediente MedOS (dictado → campos → revisión → guardar) y los
// conteos de la jornada y del Panorama se actualizan solos.
// El dictado lo interpreta el servidor con reglas (sin modelo); nada se guarda
// sin que una persona confirme. Banderas y sugerencias las calcula el servidor.

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Bot, CheckCircle2, ClipboardPlus, FileHeart, Lock, MapPin, Sparkles, UserCheck, Users,
} from 'lucide-react';

import { osActionBtn } from '@/modules/orion-shell/OsApp';
import { Card, EmptyState, ErrorNote, Loading } from '@/modules/orion-shell/ui';

import { govHealthApi } from '../api';
import type { Atencion, Dictado, ExpedienteMatch, JornadaLive, RegistroBody } from '../types';
import { BanderaChips, ESTADO_CANAL_CLS, ExpedienteDrawer, medicionesTexto } from './ExpedienteDrawer';
import { OrigenBadge, Stat, fechaCorta, fmt } from './parts';

const SERVICIOS: { tipo: string; etiqueta: string }[] = [
  { tipo: 'consulta_general', etiqueta: 'Consulta general' },
  { tipo: 'glucosa', etiqueta: 'Glucosa' },
  { tipo: 'presion_arterial', etiqueta: 'Presión arterial' },
  { tipo: 'mastografia_referida', etiqueta: 'Mastografía (referida)' },
  { tipo: 'antigeno_prostatico', etiqueta: 'Antígeno prostático' },
  { tipo: 'dental', etiqueta: 'Dental' },
  { tipo: 'vacunacion', etiqueta: 'Vacunación' },
  { tipo: 'examen_vista', etiqueta: 'Vista' },
  { tipo: 'aparato_auditivo', etiqueta: 'Auditivo' },
  { tipo: 'nutricion', etiqueta: 'Nutrición' },
];
const DESTINOS = [
  { v: 'centro_salud', l: 'Centro de Salud (SSA estatal)' },
  { v: 'imss_bienestar', l: 'IMSS-Bienestar' },
  { v: 'hospital_central', l: 'Hospital Central' },
  { v: 'cruz_roja', l: 'Cruz Roja Mexicana' },
  { v: 'otro', l: 'Otra institución' },
];
const PRIORIDADES = [{ v: 'ordinaria', l: 'Ordinaria' }, { v: 'preferente', l: 'Preferente' }, { v: 'urgente', l: 'Urgente' }];
const ESTADO_LABEL: Record<string, string> = { programada: 'Programada', en_curso: 'En curso', realizada: 'Realizada', cancelada: 'Cancelada' };

interface Form {
  nombre: string; edad: string; sexo: string; paciente: ExpedienteMatch | null;
  sis: string; dia: string; glucosa: string; ayuno: boolean; peso: string; talla: string;
  servicios: string[]; nota: string; canalizar: boolean; destino: string; prioridad: string;
}
const VACIO: Form = {
  nombre: '', edad: '', sexo: '', paciente: null, sis: '', dia: '', glucosa: '', ayuno: false, peso: '', talla: '',
  servicios: [], nota: '', canalizar: false, destino: 'centro_salud', prioridad: 'ordinaria',
};

const input = 'h-10 w-full rounded-xl border border-border bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40';
const label = 'mb-1 block text-xs font-medium text-muted-foreground';

export function JornadaView() {
  const [routeId, setRouteId] = useState('');
  const [abierto, setAbierto] = useState<string | null>(null);
  const j = useQuery({ queryKey: ['gov-health', 'jornada', routeId], queryFn: () => govHealthApi.jornada(routeId), refetchInterval: 15000 });
  // Fijar la jornada resuelta: si no, el refresco de 15 s podría saltar a otra (p. ej. cuando
  // otra brigada pone la suya «en curso») y borrar el formulario a medio llenar.
  const resuelta = j.data?.jornada?.id;
  useEffect(() => { if (!routeId && resuelta) setRouteId(resuelta); }, [routeId, resuelta]);
  if (j.isLoading) return <Loading />;
  if (j.isError) return <ErrorNote>{(j.error as Error).message}</ErrorNote>;
  const d = j.data!;
  if (!d.jornada) {
    return <EmptyState icon={<ClipboardPlus size={18} />} title="No hay jornadas de la Ruta de la Salud">Programa una jornada para registrar a las personas atendidas.</EmptyState>;
  }
  const r = d.jornada;
  return (
    <>
      <JornadaHeader r={r} esHoy={!!d.es_hoy} otras={d.otras} onPick={setRouteId} />
      {!d.puede_registrar && (
        <p className="rounded-2xl border border-dashed border-border px-4 py-3 text-sm text-muted-foreground">
          Esta jornada es el {fechaCorta(r.fecha)}: el registro de personas se abre ese día.
        </p>
      )}
      <div className="grid gap-4 lg:grid-cols-5">
        <div className="lg:col-span-2">
          {d.puede_registrar && r.estado !== 'cancelada' && r.estado !== 'realizada' ? <RegistroCard r={r} onOpen={setAbierto} /> : (
            <Card title="Registrar persona"><p className="text-sm text-muted-foreground">{r.estado === 'realizada' ? 'La jornada ya se cerró: sus registros quedan en los expedientes y en el tablero.' : 'El registro se abre el día de la jornada.'}</p></Card>
          )}
        </div>
        <div className="lg:col-span-3"><AtendidosCard routeId={r.id} onOpen={setAbierto} /></div>
      </div>
      {abierto && <ExpedienteDrawer patientId={abierto} onClose={() => setAbierto(null)} />}
    </>
  );
}

function JornadaHeader({ r, esHoy, otras, onPick }: { r: JornadaLive; esHoy: boolean; otras: { id: string; fecha: string; colonia: string }[]; onPick: (id: string) => void }) {
  const qc = useQueryClient();
  const cerrar = useMutation({
    mutationFn: () => govHealthApi.cerrarJornada(r.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gov-health'] }),
  });
  const conBandera = r.banderas.filter((b) => b.nivel !== 'baja').reduce((a, b) => a + (b.n ?? 0), 0);
  return (
    <Card
      title={<span className="inline-flex flex-wrap items-center gap-2"><ClipboardPlus size={16} className="text-primary" />
        {esHoy ? 'Caravana de hoy' : 'Jornada'} · {r.colonia}</span>}
      action={<span className="flex items-center gap-2">
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${r.estado === 'en_curso' ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' : 'bg-muted text-foreground'}`}>
          {r.estado === 'en_curso' && <span className="mr-1 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500 align-middle" />}
          {ESTADO_LABEL[r.estado] ?? r.estado}
        </span>
        <OrigenBadge origen={r.is_demo ? 'demostracion' : 'real'} />
      </span>}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
          <span>{fechaCorta(r.fecha)}</span>
          <span><MapPin size={13} className="mr-0.5 inline" />{r.sede}</span>
          <span className="truncate">{r.servicios.map((s) => s.etiqueta).join(' · ')}</span>
        </div>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Stat label="Personas registradas" value={fmt(r.registrados)} sub="cada una con su expediente MedOS" />
          <Stat label="Atendidas / cupo" value={`${fmt(r.atendidos ?? 0)} / ${fmt(r.cupo)}`} sub={r.cupo ? `${Math.round(100 * (r.atendidos ?? 0) / r.cupo)}% del cupo` : undefined} />
          <Stat label="Canalizaciones" value={fmt(r.canalizaciones)} sub="a Centro de Salud, IMSS-Bienestar…" />
          <Stat label="Fuera de rango" value={fmt(conBandera)} sub="presión o glucosa · banderas del servidor, no diagnósticos" />
        </div>
        {r.banderas.length > 0 && <BanderaChips banderas={r.banderas} />}
        <div className="flex flex-wrap items-center gap-2">
          {otras.length > 0 && (
            <select aria-label="Cambiar de jornada" className={`${input} !h-9 w-auto`} value="" onChange={(e) => e.target.value && onPick(e.target.value)}>
              <option value="">Otra jornada…</option>
              {otras.map((o) => <option key={o.id} value={o.id}>{fechaCorta(o.fecha)} · {o.colonia}</option>)}
            </select>
          )}
          {(r.estado === 'en_curso') && (
            <button type="button" className={osActionBtn} disabled={cerrar.isPending}
              onClick={() => { if (window.confirm(`¿Cerrar la jornada de ${r.colonia}? Ya no se podrán registrar personas en ella.`)) cerrar.mutate(); }}>
              <CheckCircle2 size={14} /> Cerrar jornada
            </button>
          )}
          <span className="text-[11px] text-muted-foreground">Los totales se actualizan solos cada 15 s y alimentan el Panorama y el Tablero del Presidente.</span>
        </div>
      </div>
    </Card>
  );
}

function RegistroCard({ r, onOpen }: { r: JornadaLive; onOpen: (id: string) => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState<Form>(VACIO);
  const [texto, setTexto] = useState('');
  const [dict, setDict] = useState<Dictado | null>(null);
  const [hecho, setHecho] = useState<Atencion | null>(null);
  const set = (p: Partial<Form>) => setF((x) => ({ ...x, ...p }));
  useEffect(() => { setF(VACIO); setDict(null); setHecho(null); }, [r.id]);

  const interpretar = useMutation({
    mutationFn: () => govHealthApi.dictado(texto, r.id),
    onSuccess: (d) => {
      setDict(d);
      const c = d.campos;
      const m = c.mediciones;
      setF((x) => ({
        ...x,
        nombre: c.nombre ?? x.nombre, edad: c.edad != null ? String(c.edad) : x.edad, sexo: c.sexo ?? x.sexo,
        sis: m.ta_sistolica != null ? String(m.ta_sistolica) : x.sis, dia: m.ta_diastolica != null ? String(m.ta_diastolica) : x.dia,
        glucosa: m.glucosa_capilar != null ? String(m.glucosa_capilar) : x.glucosa, ayuno: c.ayuno || x.ayuno,
        peso: m.peso != null ? String(m.peso) : x.peso, talla: m.talla != null ? String(m.talla) : x.talla,
        servicios: c.servicios.length ? c.servicios : x.servicios, nota: c.nota || x.nota,
        ...(c.canalizar ? { canalizar: true, destino: c.canalizar.destino, prioridad: c.canalizar.prioridad } : {}),
      }));
    },
  });

  const guardar = useMutation({
    mutationFn: () => {
      const med: Record<string, number | string> = {};
      if (f.sis && f.dia) { med.ta_sistolica = f.sis; med.ta_diastolica = f.dia; }
      if (f.glucosa) med.glucosa_capilar = f.glucosa;
      if (f.peso) med.peso = f.peso;
      if (f.talla) med.talla = f.talla;
      const body: RegistroBody = {
        servicios: f.servicios, mediciones: med, ayuno: f.ayuno, nota: f.nota,
        canalizar: f.canalizar ? { destino: f.destino, prioridad: f.prioridad } : null,
        ...(f.paciente ? { patient_id: f.paciente.id } : { nombre: f.nombre.trim(), edad: f.edad ? Number(f.edad) : null, sexo: f.sexo }),
      };
      return govHealthApi.registrar(r.id, body);
    },
    onSuccess: (a) => {
      setHecho(a); setF(VACIO); setTexto(''); setDict(null);
      qc.invalidateQueries({ queryKey: ['gov-health'] });
    },
  });

  const toggleServ = (t: string) => set({ servicios: f.servicios.includes(t) ? f.servicios.filter((s) => s !== t) : [...f.servicios, t] });
  const presionIncompleta = !!f.sis !== !!f.dia;
  const listo = (f.paciente || f.nombre.trim().length >= 3) && !presionIncompleta;

  return (
    <Card title="Registrar persona" action={<span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground"><Lock size={11} />Directo a MedOS</span>}>
      <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); if (listo) guardar.mutate(); }}>
        <div className="rounded-2xl border border-primary/25 bg-primary/5 p-3">
          <label htmlFor="dictado" className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold"><Bot size={15} className="text-primary" /> Díctale al agente</label>
          <textarea id="dictado" rows={3} value={texto} onChange={(e) => setTexto(e.target.value)}
            placeholder="Ej. Rosa Martínez, 64 años, mujer, glucosa 210, presión 150/95, la canalizamos al Centro de Salud"
            className="w-full resize-none rounded-xl border border-border bg-background p-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40" />
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button type="button" className={`${osActionBtn} !border-primary/40 !bg-primary !text-primary-foreground hover:!bg-primary/90`}
              disabled={!texto.trim() || interpretar.isPending} onClick={() => interpretar.mutate()}>
              <Sparkles size={14} /> {interpretar.isPending ? 'Interpretando…' : 'Llenar el registro'}
            </button>
            <span className="text-[11px] text-muted-foreground">Se interpreta en el servidor del municipio, sin modelos externos. Revisa antes de guardar.</span>
          </div>
          {interpretar.isError && <p className="mt-2 text-xs text-red-700 dark:text-red-300">{(interpretar.error as Error).message}</p>}
          {dict && <DictadoResultado d={dict} onUse={(p) => set({ paciente: p })} onApply={(s) => set({ canalizar: true, destino: s.destino, prioridad: s.prioridad })} />}
        </div>

        {f.paciente ? (
          <div className="flex items-center gap-3 rounded-xl border border-emerald-500/40 bg-emerald-500/5 px-3 py-2.5 text-sm">
            <UserCheck size={16} className="text-emerald-700 dark:text-emerald-300" />
            <span className="min-w-0 flex-1"><span className="font-semibold">{f.paciente.display_name}</span> · expediente existente ({f.paciente.visitas} visita{f.paciente.visitas === 1 ? '' : 's'})</span>
            <button type="button" className="text-xs font-medium text-primary hover:underline" onClick={() => onOpen(f.paciente!.id)}>Ver</button>
            <button type="button" className="text-xs text-muted-foreground hover:underline" onClick={() => set({ paciente: null })}>Cambiar</button>
          </div>
        ) : (
          <div className="grid grid-cols-6 gap-2">
            <div className="col-span-6 sm:col-span-3"><label className={label} htmlFor="f-nombre">Nombre</label>
              <input id="f-nombre" className={input} value={f.nombre} onChange={(e) => set({ nombre: e.target.value })} autoComplete="off" /></div>
            <div className="col-span-2 sm:col-span-1"><label className={label} htmlFor="f-edad">Edad</label>
              <input id="f-edad" className={input} inputMode="numeric" value={f.edad} onChange={(e) => set({ edad: e.target.value.replace(/\D/g, '').slice(0, 3) })} /></div>
            <div className="col-span-4 sm:col-span-2"><span className={label}>Sexo</span>
              <div className="flex gap-1" role="radiogroup" aria-label="Sexo">
                {[['F', 'Mujer'], ['M', 'Hombre']].map(([v, l]) => (
                  <button key={v} type="button" role="radio" aria-checked={f.sexo === v} onClick={() => set({ sexo: f.sexo === v ? '' : v })}
                    className={`h-10 flex-1 rounded-xl border text-sm ${f.sexo === v ? 'border-primary bg-primary/10 font-semibold text-primary' : 'border-border text-foreground hover:bg-muted'}`}>{l}</button>
                ))}
              </div></div>
          </div>
        )}

        <fieldset className="grid grid-cols-6 gap-2">
          <legend className="mb-1 text-xs font-semibold text-foreground">Mediciones</legend>
          <div className="col-span-3 sm:col-span-2"><label className={label} htmlFor="f-sis">Presión (mmHg)</label>
            <div className="flex items-center gap-1">
              <input id="f-sis" aria-label="Sistólica" className={input} inputMode="numeric" placeholder="120" value={f.sis} onChange={(e) => set({ sis: e.target.value.replace(/\D/g, '').slice(0, 3) })} />
              <span className="text-muted-foreground">/</span>
              <input aria-label="Diastólica" className={input} inputMode="numeric" placeholder="80" value={f.dia} onChange={(e) => set({ dia: e.target.value.replace(/\D/g, '').slice(0, 3) })} />
            </div></div>
          <div className="col-span-3 sm:col-span-2"><label className={label} htmlFor="f-glu">Glucosa (mg/dL)</label>
            <input id="f-glu" className={input} inputMode="numeric" value={f.glucosa} onChange={(e) => set({ glucosa: e.target.value.replace(/\D/g, '').slice(0, 3) })} />
            <label className="mt-1 inline-flex items-center gap-1.5 text-[11px] text-muted-foreground"><input type="checkbox" checked={f.ayuno} onChange={(e) => set({ ayuno: e.target.checked })} />En ayuno</label></div>
          <div className="col-span-3 sm:col-span-1"><label className={label} htmlFor="f-peso">Peso (kg)</label>
            <input id="f-peso" className={input} inputMode="decimal" value={f.peso} onChange={(e) => set({ peso: e.target.value.replace(/[^\d.]/g, '').slice(0, 5) })} /></div>
          <div className="col-span-3 sm:col-span-1"><label className={label} htmlFor="f-talla">Talla (m)</label>
            <input id="f-talla" className={input} inputMode="decimal" placeholder="1.60" value={f.talla} onChange={(e) => set({ talla: e.target.value.replace(/[^\d.]/g, '').slice(0, 4) })} /></div>
        </fieldset>

        <div><span className={label}>Servicios que recibió</span>
          <div className="flex flex-wrap gap-1.5">
            {SERVICIOS.map((s) => (
              <button key={s.tipo} type="button" aria-pressed={f.servicios.includes(s.tipo)} onClick={() => toggleServ(s.tipo)}
                className={`h-8 rounded-full border px-3 text-xs ${f.servicios.includes(s.tipo) ? 'border-primary bg-primary/10 font-semibold text-primary' : 'border-border text-foreground hover:bg-muted'}`}>{s.etiqueta}</button>
            ))}
          </div></div>

        <div><label className={label} htmlFor="f-nota">Nota (se guarda cifrada en el expediente)</label>
          <textarea id="f-nota" rows={2} value={f.nota} onChange={(e) => set({ nota: e.target.value })}
            className="w-full resize-none rounded-xl border border-border bg-background p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40" /></div>

        <div className="rounded-xl border border-border p-3">
          <label className="inline-flex items-center gap-2 text-sm font-medium"><input type="checkbox" checked={f.canalizar} onChange={(e) => set({ canalizar: e.target.checked })} />Canalizar a otra institución</label>
          {f.canalizar && (
            <div className="mt-2 grid grid-cols-2 gap-2">
              <select aria-label="Destino" className={input} value={f.destino} onChange={(e) => set({ destino: e.target.value })}>{DESTINOS.map((x) => <option key={x.v} value={x.v}>{x.l}</option>)}</select>
              <select aria-label="Prioridad" className={input} value={f.prioridad} onChange={(e) => set({ prioridad: e.target.value })}>{PRIORIDADES.map((x) => <option key={x.v} value={x.v}>{x.l}</option>)}</select>
            </div>
          )}
        </div>

        {presionIncompleta && <p className="text-xs text-amber-700 dark:text-amber-300">Falta {f.sis ? 'la diastólica' : 'la sistólica'} de la presión.</p>}
        {guardar.isError && <ErrorNote>{(guardar.error as Error).message}</ErrorNote>}
        <button type="submit" disabled={!listo || guardar.isPending}
          className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
          <FileHeart size={16} /> {guardar.isPending ? 'Guardando…' : 'Guardar en el expediente'}
        </button>

        {hecho && (
          <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/5 p-3 text-sm" role="status">
            <p className="flex items-center gap-1.5 font-semibold"><CheckCircle2 size={15} className="text-emerald-700 dark:text-emerald-300" />Guardado en el expediente de {hecho.display_name}</p>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              <BanderaChips banderas={hecho.banderas} />
              {hecho.canalizacion && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">Folio {hecho.canalizacion.folio} → {hecho.canalizacion.destino_label}</span>}
            </div>
            {hecho.sugerencia && (
              <p className="mt-1.5 text-xs text-muted-foreground">Sugerencia del servidor: canalizar a {hecho.sugerencia.destino_label} ({hecho.sugerencia.prioridad_label.toLowerCase()}). {hecho.sugerencia.razon} {hecho.sugerencia.aviso}</p>
            )}
            <button type="button" className="mt-2 text-xs font-medium text-primary hover:underline" onClick={() => onOpen(hecho.patient_id)}>Abrir expediente</button>
          </div>
        )}
      </form>
    </Card>
  );
}

function DictadoResultado({ d, onUse, onApply }: { d: Dictado; onUse: (p: ExpedienteMatch) => void; onApply: (s: { destino: string; prioridad: string }) => void }) {
  return (
    <div className="mt-3 flex flex-col gap-2 text-xs">
      {d.entendido.length > 0 && <p><span className="font-semibold">Entendí:</span> {d.entendido.join(' · ')}</p>}
      {d.faltante.length > 0 && <p className="text-muted-foreground"><span className="font-semibold">Falta:</span> {d.faltante.join(', ')}</p>}
      {d.descartado.map((x) => <p key={x} className="text-amber-700 dark:text-amber-300">No lo usé: {x}</p>)}
      {d.banderas.length > 0 && <BanderaChips banderas={d.banderas} />}
      {d.sugerencia && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl bg-background px-3 py-2">
          <span className="min-w-0 flex-1">Sugerencia: canalizar a <span className="font-semibold">{d.sugerencia.destino_label}</span> ({d.sugerencia.prioridad_label.toLowerCase()}). {d.sugerencia.razon}</span>
          <button type="button" className="font-medium text-primary hover:underline" onClick={() => onApply(d.sugerencia!)}>Aplicar</button>
        </div>
      )}
      {d.coincidencias.map((p) => (
        <div key={p.id} className="flex flex-wrap items-center gap-2 rounded-xl border border-emerald-500/40 bg-background px-3 py-2">
          <UserCheck size={14} className="text-emerald-700 dark:text-emerald-300" />
          <span className="min-w-0 flex-1">Ya tiene expediente: <span className="font-semibold">{p.display_name}</span>{p.edad != null ? `, ${p.edad} años` : ''} · {p.visitas} visita{p.visitas === 1 ? '' : 's'}
            {p.ultima_visita ? ` (última: ${fechaCorta(p.ultima_visita.fecha)}, ${p.ultima_visita.colonia})` : ''}</span>
          <button type="button" className="font-medium text-primary hover:underline" onClick={() => onUse(p)}>Usar su expediente</button>
        </div>
      ))}
    </div>
  );
}

function AtendidosCard({ routeId, onOpen }: { routeId: string; onOpen: (id: string) => void }) {
  const a = useQuery({ queryKey: ['gov-health', 'atenciones', routeId], queryFn: () => govHealthApi.atenciones(routeId), retry: false });   // sin sondeo: cada lectura deja bitácora por persona; se refresca al registrar
  return (
    <Card title={`Personas atendidas${a.data ? ` (${fmt(a.data.count)})` : ''}`}>
      {a.isLoading ? <Loading /> : a.isError ? (
        (a.error as any)?.status === 403 ? (
          <EmptyState icon={<Lock size={18} />} title="Necesitas permiso clínico">La lista es por persona: la ve el personal de salud. La coordinación ve los totales arriba.</EmptyState>
        ) : <ErrorNote>{(a.error as Error).message}</ErrorNote>
      ) : a.data!.items.length === 0 ? (
        <EmptyState icon={<Users size={18} />} title="Aún no hay personas registradas">Cada persona que se registre aquí entra a su expediente MedOS.</EmptyState>
      ) : (
        <ul className="flex flex-col divide-y divide-border">
          {a.data!.items.map((x) => (
            <li key={x.id} className="flex flex-wrap items-start gap-x-3 gap-y-1 py-3">
              <span className="w-11 shrink-0 pt-0.5 text-xs tabular-nums text-muted-foreground">{x.hora}</span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">{x.display_name}
                  <span className="ml-1.5 font-normal text-muted-foreground">{x.edad != null ? `${x.edad} años` : ''}{x.sexo ? ` · ${x.sexo}` : ''}</span></p>
                <p className="text-xs text-muted-foreground">{x.servicios.map((s) => s.etiqueta).join(' · ')}</p>
                {medicionesTexto(x.mediciones) && <p className="text-xs font-medium tabular-nums">{medicionesTexto(x.mediciones)}</p>}
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                  <BanderaChips banderas={x.banderas} />
                  {x.canalizacion && <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${ESTADO_CANAL_CLS[x.canalizacion.estado] ?? ''}`}>→ {x.canalizacion.destino_label}{x.canalizacion.prioridad === 'urgente' ? ' · urgente' : ''}</span>}
                  {x.via_agente && <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] text-primary"><Bot size={11} />vía agente</span>}
                </div>
              </div>
              <button type="button" onClick={() => onOpen(x.patient_id)} className={`${osActionBtn} !h-8 !px-2.5 text-xs`}><FileHeart size={13} /> Expediente</button>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-2 text-[11px] text-muted-foreground">Nombre, mediciones y notas viven cifrados en MedOS; ver esta lista queda en la bitácora de accesos.</p>
    </Card>
  );
}
